import PropTypes from 'prop-types';
import {
  Card,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import { useEffect, useMemo, useState, useCallback } from 'react';
import Label from '../../../components/Label';
import MetricCard from './MetricCard';
import { EmptyModuleState, LoadingPanel, SectionError } from './ManageUserDialogStates';
import { getDecisionStatus } from './utils';
import { fetchAdmin } from '../../../admin/api';
import { PolicyInspectorDrawer } from '../shared';

function toBool(value) {
  return value === true || value === 1 || value === '1' || value === 'true';
}

function PolicyTab({ userId, onRetry }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);

  const [selectedId, setSelectedId] = useState('');
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const selectedRow = useMemo(
    () => rows.find((row) => String(row.id) === String(selectedId)) || rows[0] || null,
    [rows, selectedId]
  );

  const loadPolicies = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      const res = await fetchAdmin(`/users/${userId}/policy_decisions`, {
        page,
        page_size: pageSize,
      });

      const nextRows = res?.data?.data ?? res?.data ?? [];
      const pagination = res?.data?.pagination ?? res?.pagination ?? {};

      setRows(Array.isArray(nextRows) ? nextRows : []);
      setTotal(Number(pagination.total ?? nextRows.length ?? 0));
      setLoaded(true);

      if (nextRows.length) {
        const firstId = String(nextRows[0].id);
        setSelectedId((current) => (current && nextRows.some((r) => String(r.id) === current) ? current : firstId));
      } else {
        setSelectedId('');
      }
    } catch (err) {
      setError(err?.message || 'Failed to load policy decisions.');
    } finally {
      setLoading(false);
    }
  }, [userId, page, pageSize]);

  useEffect(() => {
    loadPolicies();
  }, [loadPolicies, refreshKey]);

  useEffect(() => {
    if (rows.length && !selectedId) setSelectedId(String(rows[0].id));
    if (!rows.length) setSelectedId('');
  }, [rows, selectedId]);

  const allowedCount = useMemo(() => rows.filter((row) => toBool(row.allowed)).length, [rows]);
  const reviewCount = useMemo(() => rows.filter((row) => toBool(row.requires_review)).length, [rows]);

  if (loading && !loaded) {
    return <LoadingPanel />;
  }

  if (error) {
    return (
      <SectionError
        message={error}
        onRetry={() => {
          setRefreshKey((v) => v + 1);
          loadPolicies();
          // onRetry?.();
        }}
      />
    );
  }

  return (
    <Stack spacing={3}>
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="Decision Rows"
            value={total}
            caption="Total policy decision entries for this user."
            icon="solar:document-text-bold"
            color="primary"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="Allowed Decisions"
            value={allowedCount}
            caption="Loaded rows currently marked as allowed."
            icon="solar:check-circle-bold"
            color="success"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="Awaiting Review"
            value={reviewCount}
            caption="Loaded rows requiring review."
            icon="solar:danger-triangle-bold"
            color="warning"
          />
        </Grid>
      </Grid>

      {rows.length ? (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
              <TableContainer>
                <Table size="medium">
                  <TableHead>
                    <TableRow>
                      <TableCell>Reference</TableCell>
                      <TableCell>Scope</TableCell>
                      <TableCell>Decision</TableCell>
                      <TableCell>Tier</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => {
                      const isSelected = String(row.id) === String(selectedRow?.id);
                      const allowed = toBool(row.allowed);
                      const requiresReview = toBool(row.requires_review);

                      return (
                        <TableRow
                          hover
                          key={row.id}
                          selected={isSelected}
                          onClick={() => {
                            setSelectedId(String(row.id));
                            setInspectorOpen(true);
                          }}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell sx={{ fontFamily: 'monospace' }}>{row.reference}</TableCell>
                          <TableCell>{row.decision_scope}</TableCell>
                          <TableCell>{row.decision_code}</TableCell>
                          <TableCell>{row.tier_code}</TableCell>
                          <TableCell>
                            <Label color={requiresReview ? 'warning' : allowed ? 'success' : 'error'}>
                              {getDecisionStatus({ ...row, allowed, requires_review: requiresReview })}
                            </Label>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider />

              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, nextPage) => setPage(nextPage)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(e) => {
                  setPage(0);
                  setPageSize(parseInt(e.target.value, 10));
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </Card>
          </Grid>
        </Grid>
      ) : (
        <EmptyModuleState
          title="No policy decisions"
          description="The policy decisions endpoint returned no rows for this user."
          icon="solar:shield-warning-bold"
        />
      )}

      <PolicyInspectorDrawer
        open={inspectorOpen && Boolean(selectedRow)}
        onClose={() => setInspectorOpen(false)}
        row={selectedRow}
      />
    </Stack>
  );
}

PolicyTab.propTypes = {
  userId: PropTypes.string.isRequired,
  onRetry: PropTypes.func.isRequired,
};

export default PolicyTab;
